import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async (attempt) => {
      if (attempt >= 5) {
        setStatus('timeout');
        return;
      }

      try {
        const res = await fetch(`${BACKEND_URL}/api/payments/status/${sessionId}`);
        const data = await res.json();
        setPaymentData(data);
        setAttempts(attempt + 1);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        setTimeout(() => pollStatus(attempt + 1), 2000);
      } catch {
        setTimeout(() => pollStatus(attempt + 1), 2000);
      }
    };

    pollStatus(0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        {status === 'checking' && (
          <div className="p-12 text-center">
            <Loader2 className="w-16 h-16 text-cyan-500 animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Processing Payment</h2>
            <p className="text-gray-500">Verifying your payment... (attempt {attempts}/5)</p>
          </div>
        )}

        {status === 'success' && (
          <>
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-8 text-center">
              <CheckCircle className="w-20 h-20 text-white mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white">Payment Successful!</h2>
            </div>
            <div className="p-8 space-y-4">
              <p className="text-gray-600 text-center">
                Thank you for your payment. A receipt has been sent to your email.
              </p>
              {paymentData && (
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Amount</span>
                    <span className="text-gray-900 font-semibold">
                      {paymentData.currency?.toUpperCase()} {(paymentData.amount_total / 100).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Status</span>
                    <span className="text-emerald-600 font-medium">Paid</span>
                  </div>
                </div>
              )}
              <Button
                onClick={() => window.location.href = '/'}
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to NEURA AI
              </Button>
            </div>
          </>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <div className="bg-gradient-to-r from-red-500 to-rose-500 p-8 text-center">
              <XCircle className="w-20 h-20 text-white mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white">
                {status === 'expired' ? 'Session Expired' : 'Payment Issue'}
              </h2>
            </div>
            <div className="p-8 space-y-4">
              <p className="text-gray-600 text-center">
                {status === 'expired' 
                  ? 'This payment session has expired. Please request a new invoice.'
                  : 'There was an issue verifying your payment. Please contact support.'}
              </p>
              <Button
                onClick={() => window.location.href = '/'}
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to NEURA AI
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PaymentCancelled = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6" data-testid="payment-cancelled-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-8 text-center">
          <XCircle className="w-20 h-20 text-white mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white">Payment Cancelled</h2>
        </div>
        <div className="p-8 space-y-4">
          <p className="text-gray-600 text-center">
            Your payment was cancelled. No charges were made. You can try again using the same payment link.
          </p>
          <Button
            onClick={() => window.location.href = '/'}
            className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to NEURA AI
          </Button>
        </div>
      </div>
    </div>
  );
};

export { PaymentSuccess, PaymentCancelled };
